/**
 * `<DateTimePicker />` — Story 16.3.
 *
 * Composite primitive: typed `YYYY-MM-DD` date input + calendar popover +
 * typed `HH:mm` time input + time-stepper popover. Drop-in compatible with
 * the wizard's `<Input type='datetime-local'>` value contract: emits
 * `YYYY-MM-DDTHH:mm` strings via `onChange`.
 *
 * No third-party deps (CSD lock per story body — `react-day-picker`,
 * `react-datepicker`, `@floating-ui/react`, `date-fns` all prohibited).
 *
 * Keyboard nav follows the WAI-ARIA APG date-picker dialog pattern:
 * https://www.w3.org/WAI/ARIA/apg/patterns/datepicker-dialog/
 *   - ←/→ ±1 day, ↑/↓ ±7 days, PageUp/Down ±1 month, Shift+PageUp/Down ±1 year
 *   - Home/End start/end of week, Enter selects + closes, Esc closes.
 *
 * Time picker accepts `HH:mm`, `H:m`, `9pm`, `9:30 am`. On blur, snaps to
 * the nearest 5-minute increment. Helper line shows the day-of-week +
 * resolved local string.
 */
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

const MIN_TAP_TARGET = 44;
const MINUTE_STEP = 5;

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export interface DateTimePickerProps {
  /** Visible label rendered above the field group. */
  label: string;
  /** `YYYY-MM-DDTHH:mm` (datetime-local format) — empty string if unset. */
  value: string;
  /** Emits the same `YYYY-MM-DDTHH:mm` shape (or '' if cleared). */
  onChange: (next: string) => void;
  /** Helper text rendered below; replaced by errorText if non-empty. */
  helperText?: string;
  /** Per-field error message. Renders red, sets aria-invalid. */
  errorText?: string;
  /** Adds the `*` indicator + the `required` attr on inputs. */
  required?: boolean;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function parseValue(value: string): { date: string; time: string } {
  if (!value) return { date: '', time: '' };
  const [d, t] = value.split('T');
  return { date: d ?? '', time: (t ?? '').slice(0, 5) };
}

function buildValue(date: string, time: string): string {
  if (!date && !time) return '';
  if (!date) return '';
  const t = time || '00:00';
  return `${date}T${t}`;
}

/** Parses `9pm`, `9 PM`, `9:30 am`, `H:m`, `HH:mm` → `HH:mm` or null. */
export function parseTimeString(input: string): string | null {
  const s = input.trim().toLowerCase();
  if (s === '') return null;
  const ampm = /^(\d{1,2})(?::(\d{1,2}))?\s*(am|pm)$/.exec(s);
  if (ampm) {
    let h = Number(ampm[1]);
    const m = ampm[2] ? Number(ampm[2]) : 0;
    if (h < 1 || h > 12 || m < 0 || m > 59) return null;
    if (ampm[3] === 'pm' && h !== 12) h += 12;
    if (ampm[3] === 'am' && h === 12) h = 0;
    return `${pad2(h)}:${pad2(m)}`;
  }
  const hm = /^(\d{1,2}):(\d{1,2})$/.exec(s);
  if (hm) {
    const h = Number(hm[1]);
    const m = Number(hm[2]);
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return `${pad2(h)}:${pad2(m)}`;
  }
  return null;
}

/** Snaps `HH:mm` to nearest 5-minute increment. Returns same string if already snapped. */
export function snapToFiveMinutes(time: string): string {
  const m = /^(\d{2}):(\d{2})$/.exec(time);
  if (!m) return time;
  const h = Number(m[1]);
  const min = Number(m[2]);
  const snapped = Math.round(min / MINUTE_STEP) * MINUTE_STEP;
  if (snapped === 60) {
    const nextH = (h + 1) % 24;
    return `${pad2(nextH)}:00`;
  }
  return `${pad2(h)}:${pad2(snapped)}`;
}

/** Builds a 6-week grid of Date objects for the given year/month (0-indexed). */
function buildMonthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const startDayOfWeek = first.getDay();
  const gridStart = new Date(year, month, 1 - startDayOfWeek);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    cells.push(new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i));
  }
  return cells;
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function formatHelper(date: string, time: string): string {
  if (!date) return '';
  const [y, mo, d] = date.split('-').map(Number);
  const obj = new Date(y!, (mo ?? 1) - 1, d ?? 1);
  const dow = DAYS_OF_WEEK[obj.getDay()];
  const moStr = MONTHS[obj.getMonth()];
  const timeStr = time ? `, ${time}` : '';
  return `${dow}, ${moStr} ${obj.getDate()}, ${obj.getFullYear()}${timeStr}`;
}

export function DateTimePicker({
  label,
  value,
  onChange,
  helperText,
  errorText,
  required,
}: DateTimePickerProps) {
  const { date, time } = parseValue(value);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [focusedDate, setFocusedDate] = useState<string>(date || isoDate(new Date()));
  const [timeOpen, setTimeOpen] = useState(false);
  const [dateText, setDateText] = useState(date);
  const [timeText, setTimeText] = useState(time);

  const labelId = useId();
  const dateInputId = useId();
  const timeInputId = useId();
  const calendarTriggerRef = useRef<HTMLButtonElement | null>(null);
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const focusedCellRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    setDateText(date);
  }, [date]);
  useEffect(() => {
    setTimeText(time);
  }, [time]);

  // Move focus to focused day when the calendar opens or the focused
  // date changes while open.
  useEffect(() => {
    if (calendarOpen && focusedCellRef.current !== null) {
      focusedCellRef.current.focus();
    }
  }, [calendarOpen, focusedDate]);

  const focused = useMemo(() => {
    const [y, mo, d] = focusedDate.split('-').map(Number);
    return new Date(y!, (mo ?? 1) - 1, d ?? 1);
  }, [focusedDate]);

  const grid = useMemo(
    () => buildMonthGrid(focused.getFullYear(), focused.getMonth()),
    [focused],
  );

  function commitDate(next: string) {
    setDateText(next);
    onChange(buildValue(next, time));
  }

  function commitTime(next: string) {
    setTimeText(next);
    onChange(buildValue(date, next));
  }

  function handleDateBlur() {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateText);
    if (m) {
      commitDate(dateText);
    } else if (dateText === '') {
      commitDate('');
    } else {
      // Reset to last committed date if invalid.
      setDateText(date);
    }
  }

  function handleTimeBlur() {
    if (timeText === '') {
      commitTime('');
      return;
    }
    const parsed = parseTimeString(timeText);
    if (parsed === null) {
      setTimeText(time);
      return;
    }
    const snapped = snapToFiveMinutes(parsed);
    commitTime(snapped);
  }

  function handleCalendarKey(e: KeyboardEvent<HTMLDivElement>) {
    const [y, mo, d] = focusedDate.split('-').map(Number);
    const cur = new Date(y!, (mo ?? 1) - 1, d ?? 1);
    let next: Date | null = null;
    if (e.key === 'ArrowLeft') next = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() - 1);
    else if (e.key === 'ArrowRight') next = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
    else if (e.key === 'ArrowUp') next = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() - 7);
    else if (e.key === 'ArrowDown') next = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 7);
    else if (e.key === 'PageUp')
      next = e.shiftKey
        ? new Date(cur.getFullYear() - 1, cur.getMonth(), cur.getDate())
        : new Date(cur.getFullYear(), cur.getMonth() - 1, cur.getDate());
    else if (e.key === 'PageDown')
      next = e.shiftKey
        ? new Date(cur.getFullYear() + 1, cur.getMonth(), cur.getDate())
        : new Date(cur.getFullYear(), cur.getMonth() + 1, cur.getDate());
    else if (e.key === 'Home') next = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() - cur.getDay());
    else if (e.key === 'End') next = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + (6 - cur.getDay()));
    else if (e.key === 'Enter') {
      e.preventDefault();
      commitDate(focusedDate);
      setCalendarOpen(false);
      calendarTriggerRef.current?.focus();
      return;
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setCalendarOpen(false);
      calendarTriggerRef.current?.focus();
      return;
    } else {
      return;
    }
    e.preventDefault();
    if (next !== null) setFocusedDate(isoDate(next));
  }

  const helper = errorText
    ? errorText
    : helperText !== undefined
    ? helperText
    : formatHelper(date, time);

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-1)',
      }}
    >
      <span
        id={labelId}
        style={{
          fontSize: 'var(--text-sm-size)',
          color: 'var(--text-secondary)',
        }}
      >
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </span>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', position: 'relative' }}>
        <div style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'center' }}>
          <input
            id={dateInputId}
            type="text"
            inputMode="numeric"
            placeholder="YYYY-MM-DD"
            aria-label={`${label} date`}
            aria-invalid={errorText ? true : undefined}
            required={required}
            value={dateText}
            onChange={(e) => setDateText(e.target.value)}
            onBlur={handleDateBlur}
            style={inputStyle()}
          />
          <button
            type="button"
            ref={calendarTriggerRef}
            aria-haspopup="dialog"
            aria-expanded={calendarOpen}
            aria-label={`Open calendar for ${label}`}
            onClick={() => {
              setCalendarOpen((o) => !o);
              if (date) setFocusedDate(date);
            }}
            style={iconButtonStyle()}
          >
            📅
          </button>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'center' }}>
          <input
            id={timeInputId}
            type="text"
            inputMode="numeric"
            placeholder="HH:mm"
            aria-label={`${label} time`}
            aria-invalid={errorText ? true : undefined}
            value={timeText}
            onChange={(e) => setTimeText(e.target.value)}
            onBlur={handleTimeBlur}
            onFocus={() => setTimeOpen(true)}
            style={{ ...inputStyle(), width: 100 }}
          />
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={timeOpen}
            aria-label={`Open time picker for ${label}`}
            onClick={() => setTimeOpen((o) => !o)}
            style={iconButtonStyle()}
          >
            🕒
          </button>
        </div>
        {calendarOpen && (
          <div
            ref={calendarRef}
            role="dialog"
            aria-modal="false"
            aria-label={`Choose date for ${label}`}
            onKeyDown={handleCalendarKey}
            style={popoverStyle()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <button
                type="button"
                aria-label="Previous month"
                onClick={() => {
                  const d = new Date(focused.getFullYear(), focused.getMonth() - 1, 1);
                  setFocusedDate(isoDate(d));
                }}
                style={iconButtonStyle()}
              >
                ‹
              </button>
              <span style={{ fontWeight: 600 }}>
                {MONTHS[focused.getMonth()]} {focused.getFullYear()}
              </span>
              <button
                type="button"
                aria-label="Next month"
                onClick={() => {
                  const d = new Date(focused.getFullYear(), focused.getMonth() + 1, 1);
                  setFocusedDate(isoDate(d));
                }}
                style={iconButtonStyle()}
              >
                ›
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
              {DAYS_OF_WEEK.map((d) => (
                <span key={d} style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
                  {d}
                </span>
              ))}
              {grid.map((cell) => {
                const cellIso = isoDate(cell);
                const inMonth = cell.getMonth() === focused.getMonth();
                const isFocused = cellIso === focusedDate;
                const isSelected = cellIso === date;
                return (
                  <button
                    key={cellIso}
                    ref={isFocused ? focusedCellRef : undefined}
                    type="button"
                    tabIndex={isFocused ? 0 : -1}
                    aria-selected={isSelected}
                    aria-current={isSelected ? 'date' : undefined}
                    onClick={() => {
                      commitDate(cellIso);
                      setCalendarOpen(false);
                      calendarTriggerRef.current?.focus();
                    }}
                    style={{
                      minHeight: 32,
                      padding: 4,
                      background: isSelected ? 'var(--brand-primary)' : 'transparent',
                      color: isSelected
                        ? 'var(--brand-on-primary)'
                        : inMonth
                        ? 'var(--text-primary)'
                        : 'var(--text-muted)',
                      border: 'none',
                      borderRadius: 4,
                      fontFamily: 'var(--font-sans)',
                      fontSize: 14,
                      cursor: 'pointer',
                      outline: isFocused ? '2px solid var(--brand-primary)' : 'none',
                      outlineOffset: -2,
                    }}
                  >
                    {cell.getDate()}
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
              <button
                type="button"
                onClick={() => {
                  const today = isoDate(new Date());
                  commitDate(today);
                  setFocusedDate(today);
                  setCalendarOpen(false);
                  calendarTriggerRef.current?.focus();
                }}
                style={iconButtonStyle()}
              >
                Today
              </button>
            </div>
          </div>
        )}
        {timeOpen && (
          <div
            role="dialog"
            aria-label={`Choose time for ${label}`}
            style={{ ...popoverStyle(), top: 50 }}
          >
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                aria-label="Hour"
                value={time ? Number(time.slice(0, 2)) : 0}
                onChange={(e) => {
                  const h = Number(e.target.value);
                  const mins = time ? time.slice(3, 5) : '00';
                  commitTime(`${pad2(h)}:${mins}`);
                }}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>
                    {pad2(i)}
                  </option>
                ))}
              </select>
              <select
                aria-label="Minute"
                value={time ? Number(time.slice(3, 5)) : 0}
                onChange={(e) => {
                  const m = Number(e.target.value);
                  const hrs = time ? time.slice(0, 2) : '00';
                  commitTime(`${hrs}:${pad2(m)}`);
                }}
              >
                {Array.from({ length: 12 }, (_, i) => i * MINUTE_STEP).map((m) => (
                  <option key={m} value={m}>
                    {pad2(m)}
                  </option>
                ))}
              </select>
              <button type="button" onClick={() => setTimeOpen(false)} style={iconButtonStyle()}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
      <p
        role={errorText ? 'alert' : undefined}
        style={{
          margin: 0,
          fontSize: 'var(--text-xs-size)',
          color: errorText ? 'var(--danger)' : 'var(--text-muted)',
          minHeight: 'var(--text-xs-size)',
        }}
      >
        {helper}
      </p>
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          [role="dialog"] { transition: none !important; animation: none !important; }
        }
      `}</style>
    </div>
  );
}

function inputStyle() {
  return {
    minHeight: MIN_TAP_TARGET,
    padding: '0 var(--space-3)',
    background: 'var(--bg-surface)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-base-size)',
    outlineWidth: 2,
    outlineStyle: 'solid' as const,
    outlineColor: 'var(--brand-primary)',
    outlineOffset: 2,
  };
}

function iconButtonStyle() {
  return {
    minHeight: MIN_TAP_TARGET,
    minWidth: MIN_TAP_TARGET,
    padding: '0 var(--space-2)',
    background: 'var(--bg-surface-raised)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-base-size)',
  };
}

function popoverStyle() {
  return {
    position: 'absolute' as const,
    top: 50,
    left: 0,
    background: 'var(--bg-surface-raised)',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--radius-md)',
    padding: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    zIndex: 10,
  };
}
